import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Calendar, CheckCircle2, Send, Clock, ArrowLeft, ArrowRight } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import PublicLayout from "@/components/PublicLayout";
import CalendlyModal from "@/components/CalendlyModal";
import PhoneInput, { formatPhone } from "@/components/PhoneInput";
import usePageMeta from "@/hooks/usePageMeta";

const SLOTS = ["09:00–10:00", "11:00–12:00", "14:00–15:00", "16:00–17:00"];

export default function StrategySessionPage() {
  usePageMeta({
    title: "Schedule a Strategy Session — Luminary Insights & Advisory",
    description: "Book a 60-minute strategy session with a Luminary practice lead. Scope your decision, see our methodology, and receive a fixed-fee proposal.",
    path: "/strategy-session",
    keywords: "strategy session, executive advisory call, board advisory, scheduling",
  });
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState({
    name: "", email: "", company: "", role: "",
    objective: "", preferred_date: "", preferred_time_slot: "", timezone: "Asia/Kolkata",
  });
  const [phone, setPhone] = useState({ iso: "IN", code: "+91", number: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [err, setErr] = useState("");
  const [calOpen, setCalOpen] = useState(false);

  useEffect(() => { api.get("/settings").then((r) => setSettings(r.data)); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!phone.number.trim()) {
      setErr("Please enter a valid phone number with country code.");
      return;
    }
    setBusy(true); setErr("");
    try {
      const payload = { ...form, phone: formatPhone(phone) };
      const { data } = await api.post("/strategy-session", payload);
      setDone(data);
    } catch (e2) { setErr(formatApiError(e2)); }
    setBusy(false);
  };

  const minDate = new Date(Date.now() + 24 * 3600 * 1000).toISOString().slice(0, 10);

  return (
    <PublicLayout>
      <CalendlyModal open={calOpen} onClose={() => setCalOpen(false)} calendlyUrl={settings?.calendly_url} />

      {!done ? (
        <section className="border-b border-[var(--border)] bg-white py-12 sm:py-16" data-testid="strategy-session-page">
          <div className="max-w-[1400px] mx-auto px-4 sm:px-6">
            <Link to="/" className="btn-ghost mb-6 inline-flex" data-testid="ss-home-back"><ArrowLeft size={12} /> Home</Link>

            <div className="grid lg:grid-cols-12 gap-10 lg:gap-14">
              <div className="lg:col-span-5">
                <div className="eyebrow">Strategy Session</div>
                <h1 className="font-display font-black text-[clamp(2.4rem,5.4vw,4.4rem)] mt-4 leading-[0.95] tracking-tight text-[var(--navy)]">
                  Schedule a<br/><span className="italic font-medium text-[var(--gold)]">Strategy Session.</span>
                </h1>
                <p className="mt-6 font-display italic text-xl text-[var(--slate)] max-w-xl">
                  A 60-minute conversation with a Luminary practice lead. Scope your decision, see our
                  methodology, and decide whether to engage.
                </p>

                {/* Calendly primary path */}
                <div className="mt-9 bg-[var(--navy)] text-white p-6 sm:p-7" data-testid="calendly-cta-block">
                  <div className="eyebrow text-[var(--gold)]">Fastest path</div>
                  <h3 className="font-display font-bold text-2xl mt-3 leading-tight">Pick a slot now.</h3>
                  <p className="mt-3 text-sm text-white/75">
                    Book directly into our calendar. Confirmed instantly — no back-and-forth.
                  </p>
                  <button
                    onClick={() => setCalOpen(true)}
                    className="btn-gold mt-5"
                    data-testid="open-calendly-btn"
                  >
                    <Calendar size={14} /> Open booking calendar
                  </button>
                </div>

                <ul className="mt-9 space-y-4 text-[15px]">
                  {[
                    "Discuss the strategic question on your desk this quarter.",
                    "Walk through which of our 15 Intelligence Modules apply.",
                    "Review one or more relevant proprietary frameworks.",
                    "Receive a scoped, fixed-fee proposal — within one business day.",
                  ].map((p, i) => (
                    <li key={i} className="flex gap-3">
                      <CheckCircle2 size={18} className="text-[var(--gold)] flex-shrink-0 mt-[3px]" />
                      <span className="text-[var(--navy)]">{p}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-10 pt-8 border-t border-[var(--border)] grid sm:grid-cols-2 gap-6">
                  <div>
                    <div className="eyebrow">Direct</div>
                    <p className="mt-3 text-sm leading-relaxed text-[var(--navy)]">
                      <a
                        href={`mailto:${settings?.contact_email || "support@luminaryin.com"}`}
                        className="hover:text-[var(--gold)]"
                        data-testid="ss-direct-email"
                      >
                        {settings?.contact_email || "support@luminaryin.com"}
                      </a>
                    </p>
                  </div>
                  <div>
                    <div className="eyebrow">Response</div>
                    <p className="mt-3 text-sm leading-relaxed text-[var(--navy)]">
                      <Clock size={12} className="inline mr-1 text-[var(--gold)]" /> Within one business day.
                    </p>
                  </div>
                </div>
              </div>

              <form onSubmit={submit} className="lg:col-span-7 bg-white border border-[var(--border)] p-6 sm:p-8" data-testid="strategy-session-form">
                <div className="eyebrow">Or request manually</div>
                <h2 className="font-display font-black text-3xl mt-3 text-[var(--navy)]">Tell us what you&apos;re deciding.</h2>
                <p className="mt-2 text-sm text-[var(--slate-light)]">
                  Prefer not to use Calendly? Tell us your strategic objective and two preferred dates — we&apos;ll come back with two confirmed slot options.
                </p>

                <div className="mt-7 grid sm:grid-cols-2 gap-5">
                  <div><label className="label-mono">Name *</label><input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input-editorial" data-testid="ss-name" /></div>
                  <div><label className="label-mono">Work email *</label><input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="input-editorial" data-testid="ss-email" /></div>
                  <div><label className="label-mono">Company *</label><input required value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="input-editorial" data-testid="ss-company" /></div>
                  <div><label className="label-mono">Role *</label><input required value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="input-editorial" data-testid="ss-role" /></div>
                  <div className="sm:col-span-2">
                    <PhoneInput
                      label="Phone (with country code)"
                      required
                      value={phone}
                      onChange={setPhone}
                      testIdPrefix="ss-phone"
                    />
                  </div>
                </div>

                <div className="mt-5">
                  <label className="label-mono">Strategic objective *</label>
                  <textarea
                    required rows={5} value={form.objective}
                    onChange={(e) => setForm({ ...form, objective: e.target.value })}
                    placeholder="e.g., We are evaluating market entry into the EU specialty chemicals segment. We need to validate market sizing, identify three named entry routes, and stress-test against carbon border measures."
                    className="input-editorial resize-none"
                    data-testid="ss-objective"
                  />
                </div>

                <div className="mt-5 grid sm:grid-cols-3 gap-5">
                  <div>
                    <label className="label-mono">Preferred date</label>
                    <input type="date" min={minDate} value={form.preferred_date} onChange={(e) => setForm({ ...form, preferred_date: e.target.value })} className="input-editorial" data-testid="ss-date" />
                  </div>
                  <div>
                    <label className="label-mono">Preferred slot</label>
                    <select value={form.preferred_time_slot} onChange={(e) => setForm({ ...form, preferred_time_slot: e.target.value })} className="input-editorial" data-testid="ss-slot">
                      <option value="">Any</option>
                      {SLOTS.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="label-mono">Timezone</label>
                    <input value={form.timezone} onChange={(e) => setForm({ ...form, timezone: e.target.value })} className="input-editorial" data-testid="ss-tz" />
                  </div>
                </div>

                <div className="mt-7 flex flex-col sm:flex-row sm:items-center gap-4">
                  <button type="submit" disabled={busy} className="btn-primary" data-testid="ss-submit">
                    {busy ? "Sending…" : <><Send size={14} /> Request session</>}
                  </button>
                  {err && <p className="text-sm font-mono text-red-700" data-testid="ss-error">{err}</p>}
                </div>

                <p className="mt-5 text-[11px] font-mono uppercase tracking-[0.18em] text-[var(--slate-light)]">
                  Your request lands in our practice inbox. We respond within one business day with two confirmed slot options.
                </p>
              </form>
            </div>
          </div>
        </section>
      ) : (
        <section className="border-b border-[var(--border)] bg-white py-20 sm:py-28">
          <div className="max-w-[800px] mx-auto px-4 sm:px-6 text-center" data-testid="ss-confirmation">
            <CheckCircle2 size={44} className="mx-auto text-[var(--success)]" />
            <div className="eyebrow mt-6">Request received</div>
            <h1 className="font-display font-black text-4xl sm:text-5xl mt-4 leading-tight text-[var(--navy)]">
              Thank you. Your strategy session is in our practice queue.
            </h1>
            <p className="mt-6 text-lg text-[var(--slate)]">{done.message}</p>
            <p className="mt-3 text-sm font-mono uppercase tracking-[0.22em] text-[var(--slate-light)]">Reference: {done.id}</p>
            <div className="mt-10 flex flex-wrap justify-center gap-4">
              <Link to="/luminous" className="btn-primary" data-testid="ss-luminous-cta">Try Luminous AI <ArrowRight size={14} /></Link>
              <Link to="/" className="btn-secondary" data-testid="ss-home-cta">Back to home</Link>
            </div>
          </div>
        </section>
      )}
    </PublicLayout>
  );
}
